import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip, IconButton, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import { compact, sortBy } from "lodash";

import Permissions from "../../permissions";
import { StringUtils } from "../../util";
import { FleetContext } from "../../context";

const useStyles = makeStyles(() => ({
  truncate: {
    maxWidth: 80,
    display: "block",
    "white-space": "nowrap",
    overflow: "hidden",
    "text-overflow": "ellipsis",
  },
}));

const usePromoColumns = (promoType, handleChangeTab) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { fleets } = useContext(FleetContext);
  const classes = useStyles();

  const PROMO_GROUP = {
    canonicalName: "coupon_group_id",
    renderCellContent: (row) =>
      row.coupon_group_id && (
        <Link to={`/promo-groups/${row.coupon_group_id}`}>
          <Tooltip title={t("Promos.Links.PromoCodeGroup")}>
            <IconButton onClick={handleChangeTab} aria-label={t("Promos.Links.PromoCodeGroup")}>
              <MenuOpenIcon />
            </IconButton>
          </Tooltip>
        </Link>
      ),
  };

  const CODE = {
    name: t("Promos.Table.code"),
    canonicalName: "code",
    sortable: true,
  };

  const PERCENT_OFF = {
    name: t("Promos.Table.percent_off"),
    canonicalName: "percent_off",
    renderCellContent: (row) => StringUtils.formatNumber(row.percent_off, "PERCENT_WHOLE"),
  };

  const AMOUNT = {
    name: t("Promos.Table.amount"),
    canonicalName: "amount",
    renderCellContent: (row) => StringUtils.formatNumber(row.amount, row.currency),
  };

  const REDEEMED = {
    name: t("Promos.Table.redeemed"),
    canonicalName: "redeemed",
    renderCellContent: (row) => {
      let redeemedCounts = "";
      if (row.type === "discount") {
        redeemedCounts = `${row.customers_count}/${row.max_redemptions + row.customers_count}`;
      } else if (row.type === "unlock") {
        redeemedCounts = `${row.customers_count + row.used_by_count}/${row.max_redemptions + row.customers_count + row.used_by_count}`;
      } else {
        redeemedCounts = `${row.used_by_count}/${row.max_redemptions + row.used_by_count}`;
      }
      return redeemedCounts;
    },
  };

  const CREATED = {
    name: t("Promos.Table.created"),
    canonicalName: "created",
    renderCellContent: (row) => StringUtils.convertTz(row.created, "UTC", "UTC", "DATE_MED"),
    sortable: true,
  };

  const EXPIRED = {
    name: t("Promos.Table.expired"),
    canonicalName: "expired",
    renderCellContent: (row) => StringUtils.convertTz(row.expired, "UTC", "UTC", "DATE_MED"),
    sortable: true,
  };

  const FLEETS = {
    name: t("Promos.Table.fleets"),
    canonicalName: "fleets",
    renderCellContent: (row) => {
      let promoFleets = [];

      if (!row.fleets) {
        promoFleets = [];
      } else if (typeof row.fleets === "string") {
        promoFleets = [fleets.find(({ uuid4 }) => uuid4 === row.fleets)];
      } else if (typeof row.fleets === "object") {
        promoFleets = row.fleets.map((fleetId) => fleets.find(({ uuid4 }) => uuid4 === fleetId));
      }

      promoFleets = sortBy(compact(promoFleets), "abbreviation");

      return (
        !!promoFleets.length && (
          <Tooltip placement="top" title={promoFleets.map(({ name }) => name).join(", ")}>
            <span className={classes.truncate}>
              {promoFleets.map(({ abbreviation }) => abbreviation).join(", ")}
            </span>
          </Tooltip>
        )
      );
    },
  };

  const DESCRIPTION = {
    name: t("Promos.Table.description"),
    canonicalName: "description",
    sortable: true,
  };

  const EDIT = {
    canonicalName: "edit",
    sortable: false,
    permissions: [Permissions.readPromos, Permissions.writePromos],
    renderCellContent: (row) => (
      <Button
        component={Link}
        to={{
          pathname: `/promos/${promoType}/${row.id}/edit`,
          state: { nestedBackground: location },
        }}
      >
        {t("Promos.PromoSearch.Edit")}
      </Button>
    ),
  };

  const columns = {
    discount: [
      PROMO_GROUP,
      CODE,
      PERCENT_OFF,
      REDEEMED,
      CREATED,
      EXPIRED,
      FLEETS,
      DESCRIPTION,
      EDIT,
    ],
    bonus: [PROMO_GROUP, CODE, AMOUNT, REDEEMED, CREATED, EXPIRED, FLEETS, DESCRIPTION, EDIT],
    unlock: [PROMO_GROUP, CODE, REDEEMED, CREATED, EXPIRED, FLEETS, DESCRIPTION, EDIT],
  };

  return columns[promoType];
};

export default usePromoColumns;
